import { useEffect, useRef,useState } from 'react';

import { getImageSet, type ImageSet } from '../assets/images';

export interface OptimizedImageProps {
  src: string | ImageSet;
  alt: string;
  className?: string;
  priority?: boolean;
  sizes?: string;
  aspectRatio?: string;
  objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
}

const OptimizedImage = ({
  src,
  alt,
  className = '',
  priority = false,
  sizes,
  aspectRatio,
  objectFit = 'cover',
}: OptimizedImageProps) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(priority);
  const imgRef = useRef<HTMLDivElement>(null);

  // Resolve image source - support both string paths and ImageSet objects
  const imageSet: ImageSet | null = typeof src === 'string' ? getImageSet(src) : src;
  const fallbackSrc = imageSet?.fallback || (typeof src === 'string' ? src : src.fallback);
  const webpSrc = imageSet?.webp;

  useEffect(() => {
    if (priority) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: '50px',
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [priority]);

  const objectFitClass = {
    cover: 'object-cover',
    contain: 'object-contain',
    fill: 'object-fill',
    none: 'object-none',
    'scale-down': 'object-scale-down',
  }[objectFit];

  const handleLoad = () => {
    setIsLoaded(true);
  };

  const handleError = () => {
    console.error('[OptimizedImage] Failed to load:', src);
  };

  return (
    <div
      ref={imgRef}
      className={`relative overflow-hidden ${aspectRatio ? '' : 'w-full h-full'}`}
      style={aspectRatio ? { aspectRatio } : undefined}
    >
      {!isLoaded && (
        <div className="absolute inset-0 bg-gray-200 dark:bg-gray-700 animate-pulse" />
      )}
      {isInView ? (
        <picture>
          {webpSrc && (
            <source type="image/webp" srcSet={webpSrc} sizes={sizes} />
          )}
          <img
            src={fallbackSrc}
            alt={alt}
            className={`${className} ${objectFitClass} w-full h-full transition-opacity duration-300 ${
              isLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            loading={priority ? 'eager' : 'lazy'}
            decoding={priority ? 'sync' : 'async'}
            fetchPriority={priority ? 'high' : 'auto'}
            onLoad={handleLoad}
            onError={handleError}
            sizes={sizes}
          />
        </picture>
      ) : (
        <div className="absolute inset-0 bg-gray-300 dark:bg-gray-600" />
      )}
    </div>
  );
};

export default OptimizedImage;
